import React, { memo } from 'react';
import { exibirSentinela } from '../../utils/sentinelas';
import { useTranslation } from 'react-i18next';
import { AiOutlineArrowRight } from 'react-icons/ai';
import { formatDateTz } from '../../utils/timezone';
import { formatPriceWithSymbol } from '../../utils/currency';
import { formatPhoneDisplay } from '../../utils/phone';
import { FaMotorcycle, FaStore } from "react-icons/fa";
import { MdTableBar, MdAccessTime, MdPayment } from "react-icons/md";
import { IoMdPin } from "react-icons/io";
import {
    CardWrapper,
    CancelledOverlay,
    CardHeader,
    OrderTypeBadge,
    OrderTypeIcon,
    OrderNumber,
    OrderTime,
    TimeIcon,
    CardBody,
    CustomerSection,
    CustomerInfo,
    CustomerName,
    RecurrenceBadge,
    CustomerPhone,
    TotalSection,
    TotalLabel,
    TotalValue,
    PaymentSection,
    PaymentIcon,
    PaymentMethod,
    DeliverySection,
    DeliveryIcon,
    DeliveryAddress,
    DriverButton,
    CardActions,
    AdvanceButton,
    AdvanceIcon
} from './PedidoCard.styles';

const PedidoCard = memo(({
    pedido,
    onShowPedido,
    onAddEntregador,
    onAvancar,
    showAvancarButton = true,
    isUpdating = false,
    pedidosProcessando,
    buttonStatus = 'analysis',
    animationDelay = '0ms'
}) => {
    const { t } = useTranslation('orders');

    // Check if this specific pedido is being processed
    const isProcessing = pedidosProcessando && pedidosProcessando.has(pedido._id);

    // Determine order type
    const isPickup = pedido.entrega?.tipo_entrega === "Retirada";
    const isTable = pedido.origem_pedido === 'mesa';
    const orderType = isPickup ? 'pickup' : isTable ? 'table' : 'delivery';

    const handleCardClick = () => {
        onShowPedido(pedido._id, pedido.id_empresa);
    };

    const handleAddEntregadorClick = (event) => {
        event.stopPropagation();
        onAddEntregador(event, pedido);
    };

    const handleAvancarClick = (event) => {
        event.stopPropagation();
        onAvancar(event, pedido._id, pedido.id_pedido, pedido.status_pedido);
    };

    const formatCurrency = (value) => {
        return formatPriceWithSymbol(value || 0);
    };

    const getOrderTypeLabel = () => {
        if (isTable) return pedido.mesa_name || t('card.table');
        if (isPickup) return t('card.pickupAtStore');
        return t('filters.delivery');
    };

    const renderDeliveryInfo = () => {
        if (isTable) {
            return (
                <DeliverySection>
                    <DeliveryIcon>
                        <MdTableBar />
                    </DeliveryIcon>
                    <DeliveryAddress>{pedido.mesa_name || t('card.table')}</DeliveryAddress>
                </DeliverySection>
            );
        }

        if (isPickup) {
            return (
                <DeliverySection>
                    <DeliveryIcon>
                        <IoMdPin />
                    </DeliveryIcon>
                    <DeliveryAddress>{t('card.pickupAtStore')}</DeliveryAddress>
                </DeliverySection>
            );
        }

        // Delivery
        const address = pedido.entrega?.endereco;
        const addressText = address
            ? `${address.rua}, ${address.numero}${address.complemento ? ` - ${address.complemento}` : ''}, ${address.bairro}`
            : '';

        return (
            <DeliverySection>
                <DeliveryIcon>
                    <IoMdPin />
                </DeliveryIcon>
                <DeliveryAddress>{addressText}</DeliveryAddress>
                <DriverButton
                    onClick={handleAddEntregadorClick}
                    $placeholder={!pedido.entregador}
                >
                    <FaMotorcycle />
                    {pedido.entregador ? pedido.entregador.name : t('card.chooseDriver')}
                </DriverButton>
            </DeliverySection>
        );
    };

    return (
        <CardWrapper
            onClick={handleCardClick}
            $isProcessing={isProcessing}
            style={{ animationDelay }}
        >
            {pedido.cancelado && (
                <CancelledOverlay>
                    {t('card.cancelled')}
                </CancelledOverlay>
            )}

            <CardHeader>
                <OrderTypeBadge $type={orderType}>
                    <OrderTypeIcon>
                        {isPickup ? <FaStore /> : isTable ? <MdTableBar /> : <FaMotorcycle />}
                    </OrderTypeIcon>
                    <OrderNumber>
                        #{pedido.id_pedido_counter ?? pedido.id_pedido}
                    </OrderNumber>
                </OrderTypeBadge>

                <OrderTime>
                    <TimeIcon>
                        <MdAccessTime />
                    </TimeIcon>
                    {formatDateTz(pedido.createdAt, "HH:mm")}
                </OrderTime>
            </CardHeader>

            <CardBody>
                <CustomerSection>
                    <CustomerInfo>
                        <CustomerName>
                            {pedido.nome_cliente}
                            <RecurrenceBadge $isFirst={pedido.counter_qtd_pedido <= 1}>
                                {pedido.counter_qtd_pedido > 1 ? pedido.counter_qtd_pedido : '1º'}
                            </RecurrenceBadge>
                        </CustomerName>
                        <CustomerPhone>{formatPhoneDisplay(pedido.celular_cliente_completo || pedido.celular_cliente)}</CustomerPhone>
                    </CustomerInfo>

                    <TotalSection>
                        <TotalLabel>{t('card.total')}</TotalLabel>
                        <TotalValue>{formatCurrency(pedido.valor_total)}</TotalValue>
                    </TotalSection>
                </CustomerSection>

                <PaymentSection>
                    <PaymentIcon>
                        <MdPayment />
                    </PaymentIcon>
                    <PaymentMethod>{exibirSentinela(pedido.tipo_pagamento)}</PaymentMethod>
                </PaymentSection>

                {renderDeliveryInfo()}
            </CardBody>

            {showAvancarButton && (
                <CardActions>
                    <AdvanceButton
                        onClick={handleAvancarClick}
                        disabled={isUpdating || isProcessing}
                        $status={buttonStatus}
                    >
                        {isProcessing ? t('card.processing') : t('card.advanceOrder')}
                        {!isProcessing && (
                            <AdvanceIcon>
                                <AiOutlineArrowRight />
                            </AdvanceIcon>
                        )}
                    </AdvanceButton>
                </CardActions>
            )}
        </CardWrapper>
    );
});

PedidoCard.displayName = 'PedidoCard';

export default PedidoCard;
